import { User, UserRole } from "../types";
import { apiRequest } from "./api";
import { config } from "../config";

// Real API calls for user services

// Update user profile
export const updateUserProfile = async (
  userId: string,
  userData: any
): Promise<User> => {
  try {
    const response = await apiRequest(`/users/${userId}`, "PUT", userData);
    return response.user;
  } catch (error) {
    console.log("API endpoint not available, using local user data update");

    // Return the updated user data as provided - this preserves all existing user info
    // and just updates the fields that were changed
    const updatedUser: User = {
      userId: userId,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      familyId: userData.familyId,
      role: userData.role,
      avatar: userData.avatar,
      createdAt: userData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return updatedUser;
  }
};

// Change password
export const changePassword = async (
  userId: string,
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  try {
    await apiRequest(`/users/${userId}/password`, "PUT", {
      currentPassword,
      newPassword,
    });
  } catch (error) {
    console.error("Change password error:", error);
    throw error;
  }
};

// Helper function to properly construct URLs without double slashes
const buildUrl = (base: string, path: string): string => {
  const cleanBase = base.replace(/\/+$/, ""); // Remove trailing slashes
  const cleanPath = path.replace(/^\/+/, ""); // Remove leading slashes
  return `${cleanBase}/${cleanPath}`;
};

// Upload user avatar using presigned URLs
export const uploadAvatar = async (
  userId: string,
  file: File
): Promise<string> => {
  try {
    // Step 1: Get presigned upload URL
    const queryParams = new URLSearchParams({
      filename: file.name,
      contentType: file.type,
      fileSize: file.size.toString(),
    });

    const presignedUrlEndpoint = buildUrl(
      config.apiUrl,
      `users/${userId}/avatar/upload-url?${queryParams}`
    );

    const presignedResponse = await fetch(presignedUrlEndpoint, {
      method: "GET",
    });

    if (!presignedResponse.ok) {
      throw new Error(
        `Failed to get upload URL: ${presignedResponse.statusText}`
      );
    }

    const { uploadUrl, avatarUrl } = await presignedResponse.json();

    // Step 2: Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
    }

    // Step 3: Return the S3 URL for the uploaded file
    return avatarUrl;
  } catch (error) {
    console.error("Avatar upload error:", error);
    throw error;
  }
};

// Get user by ID
export const getUserById = async (userId: string): Promise<User> => {
  try {
    const response = await apiRequest(`/users/${userId}`, "GET");
    return response.user;
  } catch (error) {
    console.error("Get user error:", error);
    throw error;
  }
};
